import { ApiClientsError } from '#pplx/frontend/api-clients/core/errors.js';
import { API_TIMEOUT_TIERS, getQueryTimeoutMs } from '#pplx/frontend/api-clients/core/utils.js';
import { chunkForNativeRouting } from '#pplx/frontend/apps/ask/queries/mutations/bulkThreadActions.js';
import { isLocalThreadId } from '#pplx/frontend/apps/ask/thread-utils/computerThreadActions.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { threadClient } from '#pplx_generated/typespec/rest/thread/client.js';
const NEO_ONBOARDING_NEXT_STEP_CAPABILITY = 'neo_onboarding_next_step';
const batchThreadActionMutation = async ({ route, contextUUIDs, reason, failureMessage, }) => {
    const { data, error, response } = await threadClient.POST(route, reason, {
        body: { context_uuids: [...contextUUIDs] },
    });
    if (error || !data) {
        throw new ApiClientsError('API_CLIENTS_ERROR', {
            message: failureMessage,
            cause: error,
            status: response.status ?? 0,
        });
    }
    return data;
};
export const batchArchiveThreadsMutation = async (args) => batchThreadActionMutation({
    route: '/rest/thread/batch_archive_threads',
    failureMessage: 'Failed to archive threads',
    ...args,
});
export const batchUnarchiveThreadsMutation = async (args) => batchThreadActionMutation({
    route: '/rest/thread/batch_unarchive_threads',
    failureMessage: 'Failed to unarchive threads',
    ...args,
});
export const batchPinThreadsMutation = async (args) => batchThreadActionMutation({
    route: '/rest/thread/batch_pin_threads',
    failureMessage: 'Failed to pin threads',
    ...args,
});
export const batchUnpinThreadsMutation = async (args) => batchThreadActionMutation({
    route: '/rest/thread/batch_unpin_threads',
    failureMessage: 'Failed to unpin threads',
    ...args,
});
/** Matches BATCH_DELETE_MAX_NUM_THREADS enforced by DELETE /rest/thread. */
const BATCH_DELETE_CHUNK_SIZE = 64;
export const deleteThreadsMutation = async ({ entryUUIDs, contextUUIDs, rwToken, reason, }) => {
    logger.info('[deleteThreadsMutation] deleting threads', {
        entryUUIDs,
        contextUUIDs,
        reason,
    });
    const units = [
        ...entryUUIDs.map((entryUUID) => ({ entry_uuid: entryUUID })),
        ...(contextUUIDs ?? []).map((contextUUID) => ({ context_uuid: contextUUID })),
    ];
    // the endpoint rejects requests above its cap, so large selections are
    // split into chunks; a failed chunk only fails its own items.
    const isLocalUnit = (unit) => isLocalThreadId(unit.entry_uuid ?? unit.context_uuid ?? '');
    const chunks = chunkForNativeRouting(units, isLocalUnit, BATCH_DELETE_CHUNK_SIZE);
    const results = await Promise.allSettled(chunks.map(async (chunk) => {
        const { data, error, response } = await threadClient.DELETE('/rest/thread', reason, {
            body: {
                entry_uuids: chunk.flatMap((unit) => (unit.entry_uuid ? [unit.entry_uuid] : [])),
                context_uuids: chunk.flatMap((unit) => (unit.context_uuid ? [unit.context_uuid] : [])),
                read_write_token: rwToken ?? '',
            },
        });
        if (error) {
            throw new ApiClientsError('API_CLIENTS_ERROR', {
                message: 'Failed to delete threads',
                cause: error,
                status: response.status ?? 0,
            });
        }
        return data?.failed ?? [];
    }));
    if (results.every((result) => result.status === 'rejected')) {
        const firstFailure = results[0];
        logger.error('[deleteThreadsMutation] failed to delete threads', {
            entryUUIDs,
            contextUUIDs,
            reason,
            error: firstFailure?.status === 'rejected' ? firstFailure.reason : undefined,
        });
        throw firstFailure?.status === 'rejected'
            ? firstFailure.reason
            : new Error('Failed to delete threads');
    }
    const failed = results.flatMap((result, index) => {
        if (result.status === 'fulfilled')
            return [...result.value];
        return (chunks[index] ?? []).map((unit) => ({
            ...unit,
            error_code: 'DELETE_FAILED',
        }));
    });
    logger.info('[deleteThreadsMutation] deleted threads', {
        entryUUIDs,
        contextUUIDs,
        reason,
        failedCount: failed.length,
    });
    return failed;
};
export const deleteThreadMutation = async ({ entryUUID, rwToken, reason, contextUUID, }) => {
    logger.info('[deleteThreadMutation] deleting thread', {
        entryUUID,
        contextUUID,
        reason,
    });
    const { error, response } = await threadClient.DELETE('/rest/thread/delete_thread_by_entry_uuid', reason, {
        body: {
            entry_uuid: entryUUID,
            read_write_token: rwToken ?? '',
        },
    });
    if (error) {
        logger.error('[deleteThreadMutation] failed to delete thread', {
            entryUUID,
            contextUUID,
            reason,
            status: response.status ?? 0,
            error,
        });
        throw new ApiClientsError('API_CLIENTS_ERROR', {
            message: 'Failed to delete thread',
            cause: error,
            status: response.status ?? 0,
        });
    }
    logger.info('[deleteThreadMutation] deleted thread', {
        entryUUID,
        contextUUID,
        reason,
        status: response.status ?? 0,
    });
};
export const removeWidgetFromEntryMutation = async ({ backendUUID, url, rwToken, reason, }) => {
    if (!rwToken) {
        return;
    }
    const { data, error, response } = await threadClient.PUT('/rest/thread/entry/remove_widget', reason, {
        timeoutMs: getQueryTimeoutMs(),
        body: {
            entry_uuid: backendUUID,
            read_write_token: rwToken,
            url: url,
        },
    });
    if (error) {
        throw new ApiClientsError('API_CLIENTS_ERROR', {
            message: 'Error removing widget from entry',
            cause: error,
            status: response.status ?? 0,
        });
    }
    return data;
};
export const markThreadsAsViewedMutation = async ({ contextUUIDs, reason, }) => {
    const { data, error, response } = await threadClient.POST('/rest/thread/mark_viewed', reason, {
        body: {
            context_uuids: [...contextUUIDs],
        },
        timeoutMs: getQueryTimeoutMs(),
        numRetries: 1,
    });
    if (error) {
        throw new ApiClientsError('API_CLIENTS_ERROR', {
            message: 'Failed to mark threads as viewed',
            cause: error,
            status: response.status ?? 0,
        });
    }
    return data;
};
export const markThreadsAsUnreadMutation = async ({ contextUUIDs, reason, }) => {
    const { data, error, response } = await threadClient.POST('/rest/thread/mark_unread', reason, {
        body: {
            context_uuids: [...contextUUIDs],
        },
        timeoutMs: getQueryTimeoutMs(),
        numRetries: 1,
    });
    if (error) {
        throw new ApiClientsError('API_CLIENTS_ERROR', {
            message: 'Failed to mark threads as unread',
            cause: error,
            status: response.status ?? 0,
        });
    }
    return data;
};
export const replySimulatedMutation = async ({ contextUUID, action, rwToken, reason, }) => {
    const { data, error, response } = await threadClient.POST('/rest/thread/reply/simulated', reason, {
        body: {
            context_uuid: contextUUID,
            read_write_token: rwToken,
            action,
            client_capabilities: [NEO_ONBOARDING_NEXT_STEP_CAPABILITY],
        },
        timeoutMs: API_TIMEOUT_TIERS.HIGH,
    });
    if (error) {
        throw new ApiClientsError('API_CLIENTS_ERROR', {
            message: 'Error simulating turn',
            cause: error,
            status: response.status ?? 0,
        });
    }
    if (!data) {
        throw new ApiClientsError('API_CLIENTS_ERROR', {
            message: 'No data returned from reply simulated',
            status: 0,
        });
    }
    // TODO: why is this typecast necessary? there must be divergent types
    // between the backend and frontend PerplexityMessage types
    const message = {
        ...data.message,
        // TODO:
        // without this we get a destructuring error in ThreadEntryInner.tsx
        // Cannot destructure property 'mode' of '{}' as it is null.
        parent_info: data.message.parent_info || {},
    };
    return { ...data, message };
};
//# sourceMappingURL=threadMutations.js.map